import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Palette, Sun, Moon, Sparkles, RotateCcw, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

const themeIcons = {
  dark: Moon,
  light: Sun,
  vibrant: Sparkles
};

export function ThemeSettings() {
  const { theme, changeTheme, customColors, setCustomColor, resetColors, themes } = useTheme();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          style={{ 
            backgroundColor: 'var(--surface-highlight)',
            color: 'var(--text-main)'
          }}
          data-testid="theme-settings-button"
        >
          <Palette className="w-5 h-5" />
        </Button>
      </DialogTrigger>
      <DialogContent 
        className="max-w-md"
        style={{ 
          backgroundColor: 'var(--surface)',
          borderColor: 'var(--border-color)'
        }}
      >
        <DialogHeader>
          <DialogTitle 
            className="text-2xl"
            style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
          >
            Customize Theme
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Theme presets */}
          <div className="space-y-3">
            <Label style={{ color: 'var(--text-muted)' }}>Theme Presets</Label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(themes).map(([key, value]) => {
                const Icon = themeIcons[key];
                const isActive = theme === key;
                return (
                  <button
                    key={key}
                    onClick={() => changeTheme(key)}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                      isActive ? 'scale-105' : 'hover:scale-102'
                    }`}
                    style={{ 
                      backgroundColor: value.surface,
                      borderColor: isActive ? value.primary : value.border,
                      boxShadow: isActive ? `0 0 20px ${value.primary}40` : 'none'
                    }}
                    data-testid={`theme-preset-${key}`}
                  >
                    {isActive && (
                      <div 
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: value.primary }}
                      >
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <Icon 
                      className="w-6 h-6 mx-auto mb-2" 
                      style={{ color: value.primary }} 
                    />
                    <p 
                      className="text-xs font-medium text-center"
                      style={{ color: value.textMain }}
                    >
                      {value.name}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom colors */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label style={{ color: 'var(--text-muted)' }}>Custom Colors</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetColors}
                className="text-xs"
                style={{ color: 'var(--text-muted)' }}
                data-testid="reset-colors-button"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <ColorPicker
                label="Primary"
                colorKey="primary"
                value={customColors?.primary || themes[theme].primary}
                onChange={(value) => setCustomColor('primary', value)}
              />
              <ColorPicker
                label="Secondary"
                colorKey="secondary"
                value={customColors?.secondary || themes[theme].secondary}
                onChange={(value) => setCustomColor('secondary', value)}
              />
              <ColorPicker
                label="Background"
                colorKey="background"
                value={customColors?.background || themes[theme].background}
                onChange={(value) => setCustomColor('background', value)}
              />
              <ColorPicker
                label="Surface"
                colorKey="surface"
                value={customColors?.surface || themes[theme].surface}
                onChange={(value) => setCustomColor('surface', value)}
              />
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-3">
            <Label style={{ color: 'var(--text-muted)' }}>Preview</Label>
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: 'var(--background)' }}
            >
              <div 
                className="p-4 rounded-lg"
                style={{ backgroundColor: 'var(--surface)' }}
              >
                <p 
                  className="text-lg font-bold mb-2"
                  style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
                >
                  Preview Text
                </p>
                <p 
                  className="text-sm mb-3"
                  style={{ color: 'var(--text-muted)' }}
                >
                  This is how your player will look
                </p>
                <div className="flex gap-2">
                  <div 
                    className="w-8 h-8 rounded-full"
                    style={{ backgroundColor: 'var(--primary)' }}
                  />
                  <div 
                    className="w-8 h-8 rounded-full"
                    style={{ backgroundColor: 'var(--secondary)' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ColorPicker({ label, colorKey, value, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 p-1 rounded cursor-pointer"
          style={{ 
            backgroundColor: 'var(--surface-highlight)',
            borderColor: 'var(--border-color)'
          }}
          data-testid={`color-picker-${colorKey}`}
        />
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 font-mono text-xs"
          style={{ 
            backgroundColor: 'var(--surface-highlight)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-main)'
          }}
          data-testid={`color-input-${colorKey}`}
        />
      </div>
    </div>
  );
}
